import {
  FocusEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react"
import { SupportedTokenName } from "@mezo/sdk"
import {
  AlertCircle,
  Block,
  Button,
  ButtonIcon,
  ChevronDown,
  ChevronUp,
  HeadingXLarge,
  Input,
  InputProps,
  ParagraphSmall,
  useStyletron,
} from "@mezo-org/mezo-clay"

import CurrencySymbol from "#/shared/components/CurrencySymbol"

import { TokenIcon } from "../Token"

export type AmountInputProps = InputProps & {
  onButtonMax?: (e: SyntheticEvent<HTMLButtonElement>) => void
  onButtonUp?: () => void
  onButtonDown?: () => void
  onControlButtonClick?: (percentage: string) => void
  token?: SupportedTokenName
  label?: string
  unitLabel?: string
  noUnitLabelGap?: boolean
  balanceLabel?: string
  balanceUsd?: string
  balanceToken?: string
  hasBalanceError?: boolean
  errorMessage?: string
  infoMessage?: string
  isInfoMessageHighlighted?: boolean
  controlButtons?: string[]
  isMaxDisabled?: boolean
  isButtonUpDisabled?: boolean
  isButtonDownDisabled?: boolean
  isReadOnly?: boolean
}

export default function AmountInput({
  value,
  placeholder,
  token,
  label,
  unitLabel,
  noUnitLabelGap,
  balanceLabel = "Balance",
  balanceUsd,
  balanceToken,
  hasBalanceError,
  error,
  errorMessage,
  infoMessage,
  isInfoMessageHighlighted,
  disabled,
  controlButtons,
  onBlur,
  onFocus,
  onChange,
  onButtonMax,
  onButtonUp,
  onButtonDown,
  onControlButtonClick,
  isMaxDisabled,
  isButtonUpDisabled,
  isButtonDownDisabled,
  isReadOnly,
  autoFocus,
  ...inputProps
}: AmountInputProps) {
  const [css, theme] = useStyletron()
  const [hasFocus, setHasFocus] = useState(autoFocus)
  const unitLabelRef = useRef<HTMLDivElement>(null)

  const { width: unitLabelWidth = 0 } =
    unitLabelRef?.current?.getBoundingClientRect() ?? {}

  const handleFocus = useCallback(
    (ev: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setHasFocus(true)
      onFocus?.(ev)
    },
    [onFocus],
  )

  const handleBlur = useCallback(
    (ev: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setHasFocus(false)
      onBlur?.(ev)
    },
    [onBlur],
  )

  const borderColor = useMemo(() => {
    if (hasFocus) return theme.colors.contentPrimary

    if (error) return theme.colors.borderNegative

    return theme.colors.borderOpaque
  }, [
    error,
    hasFocus,
    theme.colors.borderNegative,
    theme.colors.borderOpaque,
    theme.colors.contentPrimary,
  ])

  const backgroundColor = isReadOnly
    ? theme.colors.backgroundSecondary
    : "transparent"

  return (
    <Block
      className={css({
        outline: hasFocus ? "3px solid" : "1px solid",
        outlineColor: borderColor,
        borderRadius: theme.borders.radius400,
        padding: "12px 16px",
        backgroundColor,
      })}
    >
      <Block display="flex" justifyContent="space-between">
        {label && (
          <ParagraphSmall
            color={theme.colors.contentSecondary}
            marginTop={theme.sizing.scale100}
          >
            {label}
          </ParagraphSmall>
        )}
        {token && (
          <Button
            $as="div"
            shape="pill"
            size="small"
            startEnhancer={<TokenIcon token={token} />}
            overrides={{
              // TODO: Export and use Label component
              BaseButton: {
                style: {
                  marginLeft: "auto",
                  pointerEvents: "none",
                  color: theme.colors.contentPrimary,
                  backgroundColor: theme.colors.backgroundPrimary,
                  border: "1px solid",
                  borderColor: theme.colors.borderOpaque,
                  paddingRight: theme.sizing.scale500,
                  paddingLeft: theme.sizing.scale100,
                  paddingTop: theme.sizing.scale100,
                  paddingBottom: theme.sizing.scale100,
                },
              },
            }}
          >
            <CurrencySymbol token={token} />
          </Button>
        )}
      </Block>

      <Block display="flex" alignItems="center" position="relative">
        <Input
          placeholder={placeholder}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onChange={onChange}
          value={value}
          disabled={disabled || isReadOnly}
          autoFocus={autoFocus}
          error={error}
          overrides={{
            Input: {
              style: {
                padding: "0",
                marginRight: `${unitLabelWidth + 7}px`,
                ...theme.typography.HeadingXLarge,
              },
            },
            InputContainer: {
              style: {
                backgroundColor: "transparent",
              },
            },
            Root: {
              style: {
                backgroundColor: "transparent",
                border: "none",
              },
            },
          }}
          {...inputProps}
        />

        {value && unitLabel && (
          <Block
            className={css({
              position: "absolute",
              display: "flex",
              pointerEvents: "none",
              gap: noUnitLabelGap ? 0 : "7px",
              maxWidth: "100%",
            })}
          >
            <HeadingXLarge
              className={css({
                opacity: 0,
                flex: "0 1 auto",
                display: "block",
                textWrap: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
              })}
            >
              {value}
            </HeadingXLarge>
            <Block
              ref={unitLabelRef}
              className={css({
                zIndex: 1,
                background: "#fff",
              })}
            >
              <HeadingXLarge flex="0 0 auto">{unitLabel}</HeadingXLarge>
            </Block>
          </Block>
        )}

        <Block className={css({ display: "flex", gap: theme.sizing.scale100 })}>
          {onButtonUp && (
            <ButtonIcon
              shape="square"
              size="small"
              kind="secondary"
              onClick={onButtonUp}
              disabled={isButtonUpDisabled || isReadOnly}
            >
              <ChevronUp size={16} />
            </ButtonIcon>
          )}
          {onButtonDown && (
            <ButtonIcon
              shape="square"
              size="small"
              kind="secondary"
              onClick={onButtonDown}
              disabled={isButtonDownDisabled || isReadOnly}
            >
              <ChevronDown size={16} />
            </ButtonIcon>
          )}
        </Block>
      </Block>

      {controlButtons && controlButtons.length && (
        <Block
          className={css({
            display: "flex",
            alignItems: "center",
            marginTop: theme.sizing.scale300,
            gap: theme.sizing.scale100,
          })}
        >
          {controlButtons.map((button) => (
            <Button
              key={button}
              size="xsmall"
              shape="pill"
              kind="tertiary"
              onClick={() => onControlButtonClick?.(button)}
              disabled={isReadOnly}
              overrides={{
                BaseButton: {
                  style: {
                    color: theme.colors.contentSecondary,
                    border: "1px solid",
                    borderColor: theme.colors.borderOpaque,
                  },
                },
              }}
            >
              {button}
            </Button>
          ))}
        </Block>
      )}

      <Block
        className={css({
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: theme.sizing.scale300,
          gap: theme.sizing.scale300,
        })}
      >
        {balanceUsd && (
          <ParagraphSmall
            color={theme.colors.contentSecondary}
            className={css({
              textWrap: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
            })}
          >
            ${balanceUsd}
          </ParagraphSmall>
        )}
        {balanceToken && (
          <ParagraphSmall
            className={css({
              color: theme.colors.contentSecondary,
              marginLeft: "auto",
            })}
          >
            {balanceLabel}:{" "}
            <ParagraphSmall
              as="span"
              color={
                error && hasBalanceError
                  ? theme.colors.negative
                  : theme.colors.contentPrimary
              }
              className={css({
                textWrap: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
              })}
            >
              {balanceToken} {token && <CurrencySymbol token={token} />}
            </ParagraphSmall>
          </ParagraphSmall>
        )}
        {onButtonMax && (
          <Button
            shape="pill"
            kind="secondary"
            size="xsmall"
            onClick={onButtonMax}
            disabled={isMaxDisabled || isReadOnly}
            overrides={{ BaseButton: { style: () => ({ width: "52px" }) } }}
          >
            Max
          </Button>
        )}
      </Block>
      {((error && errorMessage) || infoMessage) && (
        <Block
          className={css({
            display: "flex",
            gap: theme.sizing.scale100,
            marginTop: onButtonMax ? theme.sizing.scale100 : 0,
          })}
        >
          {(error || isInfoMessageHighlighted) && (
            <AlertCircle
              size={theme.sizing.scale600}
              color={
                error ? theme.colors.negative : theme.colors.backgroundAccent
              }
              className={css({
                flexShrink: "0",
                marginTop: theme.sizing.scale0,
              })}
            />
          )}
          <ParagraphSmall
            color={error ? theme.colors.negative : theme.colors.contentTertiary}
          >
            {error ? errorMessage : infoMessage}
          </ParagraphSmall>
        </Block>
      )}
    </Block>
  )
}
